/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';

export function getCodeBlocks(feature: BaseNode): CodeBlock[] {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const TABLE = project.getFeature(feature.getSettingValue('TABLE'));
    if (!TABLE) {
        return blocks;
    }
    const TABLE_NAME = TABLE.getSettingValue('NAME');
    const FIELDS = TABLE.getSettingValue('FIELDS');


    const FIELD_NAME = feature.getSettingValue('FIELD_NAME');
    const OPERATOR = feature.getSettingValue('OPERATOR');
    const findFunctionName = `${TABLE_NAME}_found_${feature.getID()}`;
    let links;

    links = project.getNodeLinks(feature, 'tbl_record_not_found');
    let notFound = '';
    if (links.length > 0) {
        const link = links[0];
        notFound += `/****${link.getSourceId()}****/`;
    }

    links = project.getNodeLinks(feature, 'tbl_record_find');

    const params = [];
    for (let i = 0; i < FIELDS.length; i += 1) {
        const field = FIELDS[i];
        let dataType;
        const FIELDTYPE = field.getSettingValue('FIELDTYPE');
        switch (FIELDTYPE) {
            case 'M':
            case 'E':
                dataType = TibboDataType.DWORD;
                break;
            case 'S':
                dataType = TibboDataType.STRING;
                break;
            default:
                dataType = TibboDataType.FLOAT;
                break;
        }
        const param = `${field.getSettingValue('NAME')}`;
        params.push(param);
    }

    for (let i = 0; i < links.length; i += 1) {
        const link = links[i];
        let find_index = project.getNodeParameter(link, 'find_index');
        if (find_index === '') {
            find_index = 0;
        }
        let fieldValue = project.getNodeParameter(link, 'field_value');
        for (let i = 0; i < FIELDS.length; i += 1) {
            if (FIELDS[i].getSettingValue('NAME') === FIELD_NAME) {
                const field = FIELDS[i];
                const fieldDataType = field.getSettingValue('FIELDTYPE');
                const fieldName = field.getSettingValue('NAME');
                const source: BaseNode = project.getFeature(link.getSettingValue('SOURCE_FEATURE')) as BaseNode;
                const sourceParams = source.getPort(link.getSettingValue('SOURCE_PORT'))?.params;
                switch (fieldDataType) {
                    case 'F': // Float
                        fieldValue = `${project.parseCodeExpression(project.getNodeParameterValue(feature, 'field_value'), sourceParams, TibboDataType.FLOAT)}`;
                        break;
                    case 'E': // Timestamp
                        fieldValue = `${project.parseCodeExpression(project.getNodeParameterValue(feature, 'field_value'), sourceParams, TibboDataType.DATETIME)}`;
                        break;
                    case 'M': // Time in seconds
                        fieldValue = `${project.parseCodeExpression(project.getNodeParameterValue(feature, 'field_value'), sourceParams, TibboDataType.TIME)}`;
                        break;
                    default:
                        fieldValue = `${project.parseCodeExpression(project.getNodeParameterValue(feature, 'field_value'), sourceParams, TibboDataType.STRING)}`;
                        break;
                }
                break;
            }
        }

        const wraparound = feature.getSettingValue('WRAPAROUND') === '1';
        const selectorCode = `table_find_result_index = tbl_record_find_sorted("${TABLE_NAME}",${fieldValue},"${FIELD_NAME}",${find_index},${wraparound},'${OPERATOR}')`;

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            id: 'table_find_result_index',
            template: `
let table_find_result_index;
`,
        });

        let rowValuesCode = '';
        for (let i = 0; i < params.length; i += 1) {
            let dataType = TibboDataType.STRING;
            switch(FIELDS[i].getSettingValue('FIELDTYPE')) {
                case 'F':
                    dataType = TibboDataType.FLOAT;
                    break;
                case 'E':
                    dataType = TibboDataType.DATETIME;
                    break;
                case 'M':
                    dataType = TibboDataType.TIME;
                    break;
                default:
                    dataType = TibboDataType.STRING;
                    break;
            }
            rowValuesCode += `
const ${params[i]} = ${project.convertDataType(`(rowValues[${i}]).toString()`, TibboDataType.STRING, dataType)};`;
        }

        blocks.push({
            location: link.getSourceId(),
            blockType: BlockType.CODE,
            id: link.getID(),
            template: `
${selectorCode}
if (table_find_result_index !== -1
&& table_find_result_index >= 0
&& table_find_result_index < simulator.tables["${TABLE_NAME}"].length
) {
    const rowValues = getTableRow("${TABLE_NAME}",table_find_result_index)
    ${rowValuesCode}
    ${findFunctionName}(table_find_result_index, ${params.join(',')})
} else {
    ${notFound}
}`,
        });
    }

    let foundCode = '';
    links = project.getNodeLinks(feature, 'tbl_record_found');
    if (links.length > 0) {
        const link = links[0];
        foundCode += `/****${link.getSourceId()}****/`;
    }

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        id: `function_${findFunctionName}`,
        blockType: BlockType.CODE,
        template: `
function ${findFunctionName}(row_index,${params.join(',')}) {
    ${foundCode}
}`,
    });

    return blocks;
}
